import { useEffect, useRef, useState } from 'react';
import Textfield from '@atlaskit/textfield';
import Select from '@atlaskit/select';
import Toggle from '@atlaskit/toggle';
import SharedButton from 'shared/components/buttons/button';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';
import DeliveryRequestRoutingApiService from 'core/api/delivery-request-routing/delivery-request-routing-api.service';
import {
  IPartner,
  IRequestType,
  IRequestTypeInput,
  IRoutingTable,
  RequestHandlingAction,
  RequestOversight,
} from 'core/api/delivery-request-routing/delivery-request-routing-api-interface';

/**
 * Editing one request type, as a list of rules.
 *
 * Every rule answers the same two questions — what happens to the request, and
 * whether a person confirms it — and differs only in when it applies. The first
 * rule always applies; any others apply within a period, and the backend
 * refuses two rules that cover the same moment.
 *
 * That shape is what is already stored: exactly one always-rule and any number
 * of scheduled ones. Showing it as one list rather than a default plus a set of
 * exceptions is the same information with the relationship made visible.
 */

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * One row of the editor. The first has no schedule; the rest do.
 *
 * A rule says what happens and who decides. It does not carry an address:
 * where a request goes belongs to the type, below.
 */
interface RuleDraft {
  uid?: string;
  label: string;
  days: number[];
  from: string;
  to: string;
  action: RequestHandlingAction;
  oversight: RequestOversight;
}

interface IProps {
  type: IRequestType;
  sites: string[];
  /** Everyone a request can be forwarded to, for the dropdowns. */
  partners: IPartner[];
  onSaved: (routing: IRoutingTable) => void;
}

const AddEditRequestTypeDialog = ({ type, sites, partners, onSaved }: IProps) => {
  const dialog = useDialog();

  const [label, setLabel] = useState(type.label);
  const [key, setKey] = useState(type.key);
  const [active, setActive] = useState(type.active);

  // The always-rule first, then the scheduled ones, exactly as stored.
  const [rules, setRules] = useState<RuleDraft[]>([
    {
      label: 'All of the time',
      days: [],
      from: '',
      to: '',
      action: type.defaultHandling.action,
      oversight: type.defaultHandling.oversight,
    },
    ...type.windows.map((window) => ({ ...window })),
  ]);

  // Which partner a request of this type goes to when we do not do it
  // ourselves. One for the whole type, because the team or supplier does not
  // change with the hour — and because a declined request needs somewhere to
  // go whether or not any rule forwards.
  const [forwardToPartner, setForwardToPartner] = useState<string | undefined>(type.forwardToPartner);
  const [forwardToPartnerBySite, setForwardToPartnerBySite] = useState<Record<string, string>>(
    type.forwardToPartnerBySite ?? {},
  );

  // Rendered as options once rather than per site: there is one list and
  // six site rows, and rebuilding it in each was six times the work for the
  // same array.
  const partnerOptions = partners.map((partner) => ({
    label: partner.phone ? `${partner.name} · ${partner.phone}` : `${partner.name} · no phone number`,
    value: partner.uid,
  }));
  const NOBODY = { label: 'Nobody — hold these for a person', value: '' };
  const optionFor = (uid?: string) => partnerOptions.find((option) => option.value === uid) ?? NOBODY;

  /**
   * The empty choice means something different on a pick-up site row.
   *
   * On the type it means nobody, and the request waits for a person. On a
   * pick-up site it means this site is not an exception — the type's own partner
   * handles it. Naming that partner saves reading two dropdowns to answer
   * "so where does Burton actually go?".
   */
  const inheritedName = partnerOptions.find((option) => option.value === forwardToPartner)?.label;
  const SAME_AS_ABOVE = {
    label: inheritedName ? `Same as above — ${inheritedName}` : 'Same as above — nobody set',
    value: '',
  };
  const siteOptionFor = (uid?: string) =>
    partnerOptions.find((option) => option.value === uid) ?? SAME_AS_ABOVE;

  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  // The dialog scrolls and the reasons sit at the top of it. Saving from the
  // bottom and being left looking at an unchanged form reads as nothing having
  // happened, so the reasons are brought to the reader rather than waiting to
  // be found.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const isFallback = type.isFallback;

  const update = (index: number, changes: Partial<RuleDraft>) =>
    setRules((current) => current.map((rule, at) => (at === index ? { ...rule, ...changes } : rule)));

  const toggleDay = (index: number, day: number) =>
    setRules((current) =>
      current.map((rule, at) =>
        at === index
          ? {
              ...rule,
              days: rule.days.includes(day)
                ? rule.days.filter((existing) => existing !== day)
                : [...rule.days, day].sort(),
            }
          : rule,
      ),
    );

  const duplicate = (index: number) =>
    setRules((current) => {
      const source = current[index];
      const copy: RuleDraft = {
        ...source,
        // A new identity and a distinguishable name: the copy is meant to be
        // edited, and two rules sharing a name would be unreadable on the row.
        uid: undefined,
        label: `${source.label || 'Rule'} (copy)`,
        // Copying the always-rule produces a scheduled one, since only one
        // rule can apply all of the time.
        days: index === 0 ? [] : [...source.days],
        from: index === 0 ? '' : source.from,
        to: index === 0 ? '' : source.to,
      };
      return [...current.slice(0, index + 1), copy, ...current.slice(index + 1)];
    });

  const save = async () => {
    if (!label.trim()) {
      setError('Give this request type a name.');
      return;
    }

    setSaving(true);
    setError(undefined);

    const [always, ...scheduled] = rules;

    // A site left on "nobody" is not an override; it falls through to the
    // type's partner rather than being stored as an empty choice.
    const bySite: Record<string, string> = {};
    for (const [site, partnerUid] of Object.entries(forwardToPartnerBySite)) {
      if (partnerUid) bySite[site] = partnerUid;
    }

    const input: IRequestTypeInput = {
      uid: type.uid,
      label: label.trim(),
      key: isFallback ? '' : key.trim(),
      active,
      isFallback,
      // Keys are omitted rather than set to undefined: a callable serialises
      // undefined to null on the way out. Sent whatever the rules do — this is
      // also where a declined request goes.
      ...(forwardToPartner ? { forwardToPartner } : {}),
      ...(Object.keys(bySite).length ? { forwardToPartnerBySite: bySite } : {}),
      defaultHandling: { action: always.action, oversight: always.oversight },
      windows: scheduled.map((rule) => ({
        ...(rule.uid ? { uid: rule.uid } : {}),
        label: rule.label,
        days: rule.days,
        from: rule.from,
        to: rule.to,
        action: rule.action,
        oversight: rule.oversight,
      })),
    };

    try {
      const response = await DeliveryRequestRoutingApiService.save(input);
      onSaved(response.routing);
      dialog?.closeDialog();
    } catch (caught) {
      // The dialog stays open: a refused save that closed would look like a
      // save that worked.
      setError((caught as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const buttons: ISharedDialogButton[] = [
    {
      key: 'cancel',
      type: ButtonType.Button,
      buttonProps: { onClick: () => dialog?.closeDialog(), appearance: 'subtle', label: 'Cancel', type: 'button' },
    },
    {
      key: 'save',
      type: ButtonType.Button,
      buttonProps: {
        onClick: save,
        appearance: 'primary',
        label: 'Save changes',
        type: 'button',
        isLoading: saving,
        testId: 'save-type',
      },
    },
  ];

  const choice = (testId: string, selected: boolean, heading: string, description: string, onClick: () => void) => (
    <button
      type='button'
      data-testid={testId}
      onClick={onClick}
      className={`flex-1 rounded border p-2 text-left ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
      }`}
    >
      <p className='body-02 font-semibold'>{heading}</p>
      <p className='body-01 mt-1 text-gray-600'>{description}</p>
    </button>
  );

  const ruleEditor = (rule: RuleDraft, index: number) => {
    const always = index === 0;

    return (
      <div key={rule.uid ?? index} data-testid='rule-editor' className='mt-3 rounded border p-3'>
        <div className='flex items-start gap-2'>
          <div className='grow'>
            <label className='body-01 mb-1 block font-semibold text-gray-700'>
              {always ? 'Applies all of the time' : 'What to call this rule'}
            </label>
            {always ? (
              <p className='body-01 text-gray-600'>Used whenever no other rule is in force.</p>
            ) : (
              <Textfield
                value={rule.label}
                onChange={(event) => update(index, { label: (event.target as HTMLInputElement).value })}
                placeholder='Overnight'
                testId='rule-label'
              />
            )}
          </div>
          <div className='flex shrink-0 gap-1 pt-6'>
            <SharedButton
              type='button'
              appearance='subtle'
              spacing='compact'
              label='Duplicate'
              testId='duplicate-rule'
              onClick={() => duplicate(index)}
            />
            {!always && (
              <SharedButton
                type='button'
                appearance='subtle'
                spacing='compact'
                label='Remove'
                testId='remove-rule'
                onClick={() => setRules((current) => current.filter((_, at) => at !== index))}
              />
            )}
          </div>
        </div>

        {!always && (
          <>
            <label className='body-01 mb-1 mt-3 block font-semibold text-gray-700'>Days it starts on</label>
            <div className='flex flex-wrap gap-1'>
              {DAY_NAMES.map((name, day) => (
                <button
                  key={name}
                  type='button'
                  data-testid={`rule-day-${day}`}
                  onClick={() => toggleDay(index, day)}
                  className={`rounded border px-2 py-1 ${
                    rule.days.includes(day) ? 'border-blue-500 bg-blue-50 font-semibold' : 'border-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>

            <div className='mt-3 grid grid-cols-2 gap-2'>
              <div>
                <label className='body-01 mb-1 block font-semibold text-gray-700'>From</label>
                <Textfield
                  value={rule.from}
                  onChange={(event) => update(index, { from: (event.target as HTMLInputElement).value })}
                  placeholder='20:00'
                  testId='rule-from'
                />
              </div>
              <div>
                <label className='body-01 mb-1 block font-semibold text-gray-700'>Until</label>
                <Textfield
                  value={rule.to}
                  onChange={(event) => update(index, { to: (event.target as HTMLInputElement).value })}
                  placeholder='06:00'
                  testId='rule-to'
                />
              </div>
            </div>
            <p className='body-01 mt-1 text-gray-500'>
              An end earlier than the start runs overnight into the next morning. Two rules cannot cover the same time.
            </p>
          </>
        )}

        <div className='mt-3 flex gap-2'>
          {choice(`rule-${index}-action-book`, rule.action === 'book', 'Book in-house', 'A job for our drivers.', () =>
            update(index, { action: 'book' }),
          )}
          {choice(
            `rule-${index}-action-forward`,
            rule.action === 'forward',
            'Forward it on',
            'Emailed out, and closed.',
            () => update(index, { action: 'forward' }),
          )}
        </div>

        <div className='mt-2 flex gap-2'>
          {choice(
            `rule-${index}-oversight-review`,
            rule.oversight === 'review',
            'Someone confirms',
            'Waits in the inbox.',
            () => update(index, { oversight: 'review' }),
          )}
          {choice(
            `rule-${index}-oversight-automatic`,
            rule.oversight === 'automatic',
            'Automatically',
            'Nobody looks at it.',
            () => update(index, { oversight: 'automatic' }),
          )}
        </div>

      </div>
    );
  };

  return (
    <SharedDialogBase
      title={`Edit ${type.label}`}
      customButtons={buttons}
      customContentTemplate={
        <div className='max-h-[65vh] overflow-y-auto p-4'>
          {error && (
            <div
              ref={errorRef}
              data-testid='type-form-error'
              className='mb-4 rounded border border-red-300 bg-red-50 p-3'
            >
              <p className='body-02 font-semibold text-red-800'>This could not be saved</p>
              {/* One line per problem: a single run-on string is not read to
                  the end of, and each of these is separately fixable. */}
              {error.split('\n').map((line) => (
                <p key={line} data-testid='form-error-line' className='body-01 mt-1 text-red-700'>
                  {line}
                </p>
              ))}
            </div>
          )}

          <label className='body-01 mb-1 block font-semibold text-gray-700'>Name</label>
          <Textfield
            value={label}
            onChange={(event) => setLabel((event.target as HTMLInputElement).value)}
            placeholder='What you call this kind of request'
            testId='type-label-input'
          />

          {isFallback ? (
            <p className='body-01 mt-3 text-gray-600'>
              This is the catch-all. It handles any request type that no other row matches, so it has no answer of its
              own.
            </p>
          ) : (
            <>
              <label className='body-01 mb-1 mt-4 block font-semibold text-gray-700'>
                The answer it matches, exactly as the form sends it
              </label>
              <Textfield
                value={key}
                onChange={(event) => setKey((event.target as HTMLInputElement).value)}
                placeholder='Courier - Medication'
                testId='type-key-input'
              />
              <p className='body-01 mt-1 text-gray-500'>
                A request matches this only if its first answer is exactly this text. Anything else falls to the
                catch-all.
              </p>
            </>
          )}

          <p className='body-02 mt-6 font-semibold'>Rules</p>
          <p className='body-01 mt-1 text-gray-500'>
            The first rule applies whenever no other is in force. Add more to handle requests differently at certain
            times.
          </p>

          {rules.map(ruleEditor)}

          <div className='mt-3'>
            <SharedButton
              type='button'
              appearance='default'
              spacing='compact'
              label='Add a rule'
              testId='add-rule'
              onClick={() =>
                setRules((current) => [
                  ...current,
                  { label: '', days: [], from: '', to: '', action: 'book', oversight: 'review' },
                ])
              }
            />
          </div>

          <p className='body-02 mt-6 font-semibold'>Where these go if we do not do them</p>
          <p className='body-01 mt-1 text-gray-500'>
            Used when a rule above forwards a request, and when someone declines one in the inbox. Without an address,
            requests of this type cannot be forwarded or declined, and will wait for a person.
          </p>
          <div className='mt-2' data-testid='type-forward-partner'>
            <Select
              options={[NOBODY, ...partnerOptions]}
              value={optionFor(forwardToPartner)}
              onChange={(option) => setForwardToPartner((option as { value: string })?.value || undefined)}
              placeholder='Choose a partner'
            />
          </div>
          <p className='body-01 mt-1 text-gray-500'>
            Partners are set up above, so their address and number are entered once. Each option shows the number
            requesters will be given &mdash; or says the partner has none yet.
          </p>

          <p className='body-01 mt-4 font-semibold text-gray-700'>Different address for a pick-up site</p>
          <p className='body-01 mt-1 text-gray-500'>
            Optional. Chosen by where the work is collected from, not where it is going. A pick-up site left as{' '}
            <em>same as above</em> is not an exception &mdash; it goes to the partner above. Set one only where that
            site is served by somebody different.
          </p>
          {sites.map((site) => (
            <div key={site} className='mt-2 rounded border p-2'>
              <p className='body-01 font-semibold text-gray-700'>{site}</p>
              <div className='mt-1' data-testid='site-forward-partner'>
                <Select
                  options={[SAME_AS_ABOVE, ...partnerOptions]}
                  value={siteOptionFor(forwardToPartnerBySite[site])}
                  onChange={(option) =>
                    setForwardToPartnerBySite((current) => ({
                      ...current,
                      [site]: (option as { value: string })?.value || '',
                    }))
                  }
                  placeholder='Same as above'
                />
              </div>
            </div>
          ))}

          {!isFallback && (
            <div className='mt-6 rounded border p-3'>
              <div className='flex items-center gap-3'>
                <span className='shrink-0'>
                  <Toggle isChecked={active} onChange={() => setActive((current) => !current)} testId='type-active' />
                </span>
                <p className='body-02 font-semibold'>{active ? 'In use' : 'Not in use'}</p>
              </div>
              <p className='body-01 mt-2 text-gray-600'>
                {active
                  ? 'Requests answering this are handled by the rules above.'
                  : 'This row is ignored, and requests answering this fall to the catch-all to wait for a person.'}
              </p>
            </div>
          )}
        </div>
      }
    />
  );
};

export default AddEditRequestTypeDialog;
